import { fmt, kg2 } from "@/lib/api";
import { useNavigate } from "react-router-dom";
import { ArrowUpRight } from "@phosphor-icons/react";
import { CountUp } from "@/components/CountUp";
import { Sparkline } from "@/components/Sparkline";

const TONE = { good: "hsl(var(--tone-good))", warn: "hsl(var(--tone-warn))", bad: "hsl(var(--tone-bad))", info: "hsl(var(--tone-info))", brand: "hsl(var(--primary))" };
const isKg = (u) => /^(kg|kgs|kg\/.*|kgs?\b.*)$/i.test(String(u || "").trim());

// tone: good | warn | bad | info | brand → coloured left border + icon tint. hero: bigger number. spark: array for sparkline.
export const MetricCard = ({ label, value, unit, sub, accent, icon: Icon, testid, to, color, tone, hero = false, spark, sparkKey, animate = true }) => {
  const navigate = useNavigate();
  const clickable = !!to;
  const c = color || (tone ? TONE[tone] : null);
  const text = typeof value === "number" ? (isKg(unit) ? kg2(value) : fmt(value)) : value;
  return (
    <div
      data-testid={testid}
      data-tone={tone || undefined}
      onClick={clickable ? () => navigate(to) : undefined}
      role={clickable ? "button" : undefined}
      tabIndex={clickable ? 0 : undefined}
      onKeyDown={clickable ? (e) => { if (e.key === "Enter") navigate(to); } : undefined}
      style={c ? { borderLeftWidth: 4, borderLeftColor: c } : undefined}
      className={`group metric-card border border-border/80 bg-card rounded-md ${hero ? "p-5" : "p-4"} h-full flex flex-col justify-between shadow-sm transition-[transform,box-shadow,border-color] duration-200 ${clickable ? "cursor-pointer hover:-translate-y-0.5 hover:border-primary/60 hover:shadow-[0_8px_24px_rgba(15,23,42,0.09)]" : "hover:border-primary/30"}`}
    >
      <div className="flex items-start justify-between">
        <div className="text-label font-bold">{label}</div>
        {Icon && (
          <div className={`h-7 w-7 rounded-md flex items-center justify-center ${c ? "" : (accent ? "bg-primary/10 text-primary" : "bg-muted text-muted-foreground")}`}
               style={c ? { backgroundColor: `color-mix(in srgb, ${c} 14%, transparent)`, color: c } : undefined}>
            <Icon size={16} weight="bold" />
          </div>
        )}
      </div>
      <div className="mt-3 flex items-end justify-between gap-3">
        <div className="min-w-0">
          <div className={`font-mono font-bold tracking-tight tabular-nums leading-none ${hero ? "text-[30px] sm:text-[36px]" : "text-[22px] sm:text-[24px]"}`} style={hero && c ? { color: c } : undefined}>
            {animate && typeof text === "string" ? <CountUp value={text} /> : text}
            {unit && <span className="text-[12px] font-medium text-muted-foreground ml-1 font-sans">{unit}</span>}
          </div>
          {sub && <div className="text-[11px] text-muted-foreground mt-1.5">{sub}</div>}
          {clickable && (
            <div className="font-mono text-[10px] text-primary mt-2 flex items-center gap-1 opacity-0 group-hover:opacity-100 transition-opacity" data-testid={`${testid}-cta`}>
              View entries <ArrowUpRight size={11} weight="bold" />
            </div>
          )}
        </div>
        {spark && spark.length > 1 && <Sparkline data={spark} dataKey={sparkKey} color={c || "hsl(var(--primary))"} width={hero ? 120 : 80} height={hero ? 36 : 28} testid={testid ? `${testid}-spark` : undefined} />}
      </div>
    </div>
  );
};
